import { Download, Mail, ExternalLink, Sparkles } from "lucide-react";
import FaqCarousel from "./FaqCarousel";
import MorePhotosGallery from "./MorePhotosGallery";

const STATUS_META = {
  available: { label: "Available",  cls: "bg-[#3E8054] text-white" },
  pending:   { label: "Pending",    cls: "bg-[#B77A2A] text-white" },
  placed:    { label: "Placed",     cls: "bg-[#8D6E63] text-white" },
};

export default function AnimalListing({ animal, inquiryEmail }) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const featured = animal.featured_photo
    ? (animal.featured_photo.startsWith("http") ? animal.featured_photo : `${backendUrl}${animal.featured_photo}`)
    : null;
  const status = STATUS_META[animal.status] || STATUS_META.available;
  const isPlaced = animal.status === "placed";
  const faqPdf = animal.faq_pdf ? (animal.faq_pdf.startsWith("http") ? animal.faq_pdf : `${backendUrl}${animal.faq_pdf}`) : null;
  const firstFaqPage = (animal.faq_pages || []).filter(Boolean)[0];
  const firstFaqUrl = firstFaqPage ? (firstFaqPage.startsWith("http") ? firstFaqPage : `${backendUrl}${firstFaqPage}`) : null;

  const mail = inquiryEmail || "hello@thesaltymoo.com";
  const subject = encodeURIComponent(`Inquiry — ${animal.name} (${animal.animal_type})`);
  const body = encodeURIComponent(
    `Hello,\n\nI'm interested in learning more about ${animal.name} (${animal.animal_type}, ${animal.price}${animal.price_note ? " " + animal.price_note : ""}).\n\nCould you share additional information, photos, videos, health records, or arrange a FaceTime visit?\n\nThank you,\n`
  );
  const inquireHref = `mailto:${mail}?subject=${subject}&body=${body}`;

  return (
    <article
      data-testid={`animal-${animal.slug || animal.id}`}
      className={`relative z-40 bg-[#F9F6F0] border border-[#E7DFD5] rounded-md page-shadow overflow-hidden transition-opacity ${isPlaced ? "opacity-70" : ""}`}
    >
      {isPlaced && (
        <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-r from-[#8D6E63] via-[#A87149] to-[#8D6E63] text-[#F7F0E4] text-center py-2 font-display tracking-[0.18em] text-sm sm:text-base flex items-center justify-center gap-2">
          <Sparkles size={16} /> FOUND THEIR NEW PASTURE <Sparkles size={16} />
        </div>
      )}

      <div className="p-6 sm:p-10">
        {/* Header row: featured photo + name/type/price/badges */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-10 items-start">
          <div className="lg:col-span-2">
            {featured ? (
              <img
                src={featured}
                alt={animal.name}
                className="w-full h-auto rounded-md border border-[#A87149]/30 page-shadow"
                loading="lazy"
              />
            ) : (
              <div className="w-full aspect-[4/3] rounded-md bg-[#F5EFE1] border border-dashed border-[#A87149]/40 flex items-center justify-center text-[#6D5A50] font-body text-sm">
                Photo coming soon
              </div>
            )}
          </div>
          <div className="lg:col-span-3">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className={`px-3 py-1 rounded-full text-xs font-body tracking-wide uppercase ${status.cls}`}>{status.label}</span>
              {animal.is_bonded_pair && (
                <span className="px-3 py-1 rounded-full text-xs font-body tracking-wide uppercase bg-[#B33A2A] text-white">
                  {animal.bonded_notice || "Bonded Pair — Not Offered Separately"}
                </span>
              )}
            </div>
            <h3 className="font-display text-4xl sm:text-5xl text-[#4B3A2F] leading-tight">{animal.name}</h3>
            <p className="font-script text-2xl text-[#8D6E63] mt-1">{animal.animal_type}</p>
            <p className="font-display text-3xl text-[#B33A2A] mt-4">
              {animal.price}
              {animal.price_note && <span className="font-body text-base text-[#6D5A50] ml-2 italic">{animal.price_note}</span>}
            </p>
            {animal.description && (
              <p className="font-body text-[#3A2F26] mt-5 leading-relaxed whitespace-pre-line">{animal.description}</p>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              {firstFaqUrl && (
                <a
                  data-testid={`view-faq-${animal.slug}`}
                  href={firstFaqUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4B3A2F] text-[#F7F0E4] font-body text-sm hover:bg-[#3A2F26] transition"
                >
                  <ExternalLink size={16} /> View Full FAQ
                </a>
              )}
              {faqPdf && (
                <a
                  data-testid={`download-faq-${animal.slug}`}
                  href={faqPdf}
                  download
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D6BFA0] text-[#4B3A2F] border border-[#A87149] font-body text-sm hover:bg-[#C8A876] transition"
                >
                  <Download size={16} /> Download FAQ
                </a>
              )}
              {!isPlaced && (
                <a
                  data-testid={`inquire-${animal.slug}`}
                  href={inquireHref}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#B33A2A] text-white font-body text-sm hover:bg-[#8B2C1F] transition"
                >
                  <Mail size={16} /> Inquire About {animal.name}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* FAQ carousel */}
        <div className="mt-10">
          <p className="font-script text-2xl text-[#4B3A2F] mb-4 text-center">FAQ Flyer</p>
          <div className="max-w-md mx-auto">
            <FaqCarousel pages={animal.faq_pages || []} animalName={animal.name} />
          </div>
        </div>

        {/* More Photos */}
        <div className="mt-12">
          <div className="text-center mb-6">
            <p className="font-display text-3xl text-[#4B3A2F] tracking-wider">MORE PHOTOS</p>
            <div className="gold-rule mt-3 max-w-xs mx-auto" />
          </div>
          <MorePhotosGallery photos={animal.gallery || []} animalName={animal.name} />
        </div>
      </div>
    </article>
  );
}
